import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface DispatchAssignment {
  id?: number;
  dispatchNumber: string;
  batchTicketId: number | null;
  truckId: number | null;
  driverId: number | null;

  // Schedule
  scheduledDispatchTime: Date;
  scheduledReturnTime: Date;
  actualDispatchTime: Date | null;
  actualReturnTime: Date | null;

  // Load Details
  loadNumber: number; // Which load in the batch (1, 2, 3, etc.)
  yardsLoaded: number;
  mixDesignId: number | null;
  batchNumber: string;

  // Pre-Dispatch Checks
  truckInspectionComplete: boolean;
  drumCleaned: boolean;
  waterTankFilled: boolean;
  ticketPrinted: boolean;

  // Delivery
  deliveryAddress: string;
  estimatedTravelTime: number; // minutes
  actualTravelTime: number | null; // minutes
  gpsTrackingEnabled: boolean;

  // Communication
  customerContactName: string;
  customerContactPhone: string;
  driverNotes: string;
  dispatcherNotes: string;

  // Quality
  slumpAtPlant: number | null;
  slumpAtSite: number | null;
  temperatureAtPlant: number | null;
  temperatureAtSite: number | null;

  // Status
  status: "scheduled" | "loading" | "dispatched" | "arrived" | "unloading" | "returning" | "completed" | "cancelled";
  priority: "normal" | "high" | "urgent";

  // Issues
  delayReason: string;
  qualityIssues: string;
}

class TruckDispatchWorkflowStore {
  rootStore: RootStore;

  // Observables
  loading = false;
  error: string | null = null;
  success: string | null = null;
  activeStep = 0;
  dispatchData: DispatchAssignment;

  // Dashboard view
  todayDispatches: any[] = [];
  availableTrucks: any[] = [];
  availableDrivers: any[] = [];

  constructor(rootStore: RootStore) {
    this.dispatchData = this.getDefaultDispatch();
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  getDefaultDispatch = (): DispatchAssignment => ({
    dispatchNumber: this.generateDispatchNumber(),
    batchTicketId: null,
    truckId: null,
    driverId: null,

    scheduledDispatchTime: new Date(),
    scheduledReturnTime: new Date(Date.now() + 2 * 60 * 60 * 1000), // +2 hours
    actualDispatchTime: null,
    actualReturnTime: null,

    loadNumber: 1,
    yardsLoaded: 10,
    mixDesignId: null,
    batchNumber: "",

    truckInspectionComplete: false,
    drumCleaned: false,
    waterTankFilled: false,
    ticketPrinted: false,

    deliveryAddress: "",
    estimatedTravelTime: 30, // minutes
    actualTravelTime: null,
    gpsTrackingEnabled: true,

    customerContactName: "",
    customerContactPhone: "",
    driverNotes: "",
    dispatcherNotes: "",

    slumpAtPlant: null,
    slumpAtSite: null,
    temperatureAtPlant: null,
    temperatureAtSite: null,

    status: "scheduled",
    priority: "normal",

    delayReason: "",
    qualityIssues: "",
  });

  generateDispatchNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    const hour = now.getHours().toString().padStart(2, "0");
    const minute = now.getMinutes().toString().padStart(2, "0");
    return `D${year}${month}${day}-${hour}${minute}`;
  };

  // Actions
  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  updateDispatchData = <K extends keyof DispatchAssignment>(
    field: K,
    value: DispatchAssignment[K]
  ) => {
    this.dispatchData[field] = value;

    // Auto-calculate scheduled return time based on travel time
    if (field === "estimatedTravelTime") {
      const dispatchTime = this.dispatchData.scheduledDispatchTime.getTime();
      const returnTime = dispatchTime + (this.dispatchData.estimatedTravelTime * 2 * 60 * 1000); // Round trip
      this.dispatchData.scheduledReturnTime = new Date(returnTime);
    }
  };

  togglePreDispatchCheck = (field: keyof Pick<DispatchAssignment, "truckInspectionComplete" | "drumCleaned" | "waterTankFilled" | "ticketPrinted">) => {
    this.dispatchData[field] = !this.dispatchData[field];
  };

  updateStatus = (status: DispatchAssignment["status"]) => {
    this.dispatchData.status = status;

    // Auto-set actual times when status changes
    if (status === "dispatched" && !this.dispatchData.actualDispatchTime) {
      this.dispatchData.actualDispatchTime = new Date();
    }
    if (status === "completed" && !this.dispatchData.actualReturnTime) {
      this.dispatchData.actualReturnTime = new Date();

      // Calculate actual travel time
      if (this.dispatchData.actualDispatchTime) {
        const travelMs = this.dispatchData.actualReturnTime.getTime() - this.dispatchData.actualDispatchTime.getTime();
        this.dispatchData.actualTravelTime = Math.round(travelMs / (60 * 1000)); // minutes
      }
    }
  };

  // Load batch ticket details
  loadBatchTicket = async (ticketId: number) => {
    // TODO: Load batch ticket details when service is available
    // For now, just set the ID
    this.dispatchData.batchTicketId = ticketId;
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // Batch Ticket & Load
        if (!this.dispatchData.batchTicketId) {
          this.setError("Please select a batch ticket");
          return false;
        }
        if (this.dispatchData.loadNumber <= 0) {
          this.setError("Load number must be greater than zero");
          return false;
        }
        if (this.dispatchData.yardsLoaded <= 0) {
          this.setError("Yards loaded must be greater than zero");
          return false;
        }
        break;

      case 1: // Truck & Driver Assignment
        if (!this.dispatchData.truckId) {
          this.setError("Please assign a truck");
          return false;
        }
        if (!this.dispatchData.driverId) {
          this.setError("Please assign a driver");
          return false;
        }
        break;

      case 2: // Pre-Dispatch Checks
        if (!this.dispatchData.truckInspectionComplete) {
          this.setError("Truck inspection must be completed");
          return false;
        }
        if (!this.dispatchData.drumCleaned) {
          this.setError("Drum must be cleaned before dispatch");
          return false;
        }
        if (!this.dispatchData.waterTankFilled) {
          this.setError("Water tank must be filled");
          return false;
        }
        if (!this.dispatchData.ticketPrinted) {
          this.setError("Batch ticket must be printed");
          return false;
        }
        break;

      case 3: // Delivery Details
        if (!this.dispatchData.deliveryAddress) {
          this.setError("Delivery address is required");
          return false;
        }
        if (!this.dispatchData.customerContactName) {
          this.setError("Customer contact name is required");
          return false;
        }
        break;
    }

    return true;
  };

  // Async actions
  loadInitialData = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const trucksStore = this.rootStore.trucksStore as ServiceStore;
      const driversStore = this.rootStore.driversStore as ServiceStore;

      await Promise.all([
        trucksStore?.find({ query: { status: "available" } }),
        driversStore?.find({ query: { status: "active" } }),
        this.loadTodayDispatches(),
      ]);

      runInAction(() => {
        this.availableTrucks = trucksStore?.entities || [];
        this.availableDrivers = driversStore?.entities || [];
      });
    } catch (err: any) {
      runInAction(() => {
        this.setError("Failed to load initial data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  loadTodayDispatches = async () => {
    // TODO: Load today's dispatches when service is available
    runInAction(() => {
      this.todayDispatches = [];
    });
  };

  submitDispatch = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.setLoading(true);
    this.setError(null);

    try {
      // TODO: Create dispatch assignment when backend service is available
      // This would create a truck_dispatches record

      // Update truck status to "dispatched"
      if (this.dispatchData.truckId) {
        const trucksStore = this.rootStore.trucksStore as ServiceStore;
        await trucksStore?.patch(this.dispatchData.truckId, {
          status: "dispatched",
          currentDispatchId: this.dispatchData.id,
        });
      }

      runInAction(() => {
        this.setSuccess("Truck dispatched successfully!");
        this.activeStep = 4; // Success step
      });

    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to dispatch truck");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  reset = () => {
    this.loading = false;
    this.error = null;
    this.success = null;
    this.activeStep = 0;
    this.dispatchData = this.getDefaultDispatch();
  };

  // Computed values
  get trucks() {
    const trucksStore = this.rootStore.trucksStore as ServiceStore;
    return trucksStore?.entities || [];
  }

  get drivers() {
    const driversStore = this.rootStore.driversStore as ServiceStore;
    return driversStore?.entities || [];
  }

  get selectedTruck() {
    if (!this.dispatchData.truckId) return null;
    return this.trucks.find((t: any) => t.id === this.dispatchData.truckId);
  }

  get selectedDriver() {
    if (!this.dispatchData.driverId) return null;
    return this.drivers.find((d: any) => d.id === this.dispatchData.driverId);
  }

  get preDispatchChecksComplete() {
    return (
      this.dispatchData.truckInspectionComplete &&
      this.dispatchData.drumCleaned &&
      this.dispatchData.waterTankFilled &&
      this.dispatchData.ticketPrinted
    );
  }

  get estimatedReturnTime() {
    if (!this.dispatchData.actualDispatchTime) {
      return this.dispatchData.scheduledReturnTime;
    }
    const dispatchTime = this.dispatchData.actualDispatchTime.getTime();
    const returnTime = dispatchTime + (this.dispatchData.estimatedTravelTime * 2 * 60 * 1000);
    return new Date(returnTime);
  }

  get statusColor() {
    const colors: Record<DispatchAssignment["status"], string> = {
      scheduled: "#9e9e9e",
      loading: "#2196f3",
      dispatched: "#ff9800",
      arrived: "#9c27b0",
      unloading: "#ff5722",
      returning: "#607d8b",
      completed: "#4caf50",
      cancelled: "#f44336",
    };
    return colors[this.dispatchData.status];
  }

  get priorityColor() {
    const colors: Record<DispatchAssignment["priority"], string> = {
      normal: "#2196f3",
      high: "#ff9800",
      urgent: "#f44336",
    };
    return colors[this.dispatchData.priority];
  }
}

export default TruckDispatchWorkflowStore;
